/**
 * @module Storage
 * @since 2.0.0
 * @requires memoizer
 * @requires env
 */

import { memoizer } from '../../mixin';
import { env } from '../../environment';

/**
 * @class
 * @classdesc Common interface for both `localStorage` and `sessionStorage`, failsafing usage for non-compliant browsers (e.g. incognito Safari), and basic methods.
 *
 * @property {Boolean} available Whether the storage system is available or not.
 */
export class Storage {
    constructor(key) {
        memoizer(this, 'available');

        try {
            this.storage = env.browser && window[key];
        } catch (e) { /**/ }

        [
            'get',
            'set'
        ].forEach((fn) => this[fn] = this[fn].bind(this));
    }

    get available() {
        if (!env.browser || !this.storage) {
            return false;
        }

        const key = Math.random().toString(36).substring(2);

        try {
            this.storage.setItem(key, true);
            this.storage.removeItem(key);
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Safely fetches data from the storage system, attempting to parse it from JSON.
     * @param  {String} key Key name
     * @return {*}
     */
    get(key) {
        if (!this.available) {
            return;
        }

        const value = this.storage.getItem(key);

        try {
            return JSON.parse(value);
        } catch (error) {
            return value;
        }
    }

    /**
     * Safely stores data to the storage system.
     * @param  {String} key Key name
     * @param  {*} value Value to store
     * @return {Promise<undefined>}
     */
    set(key, value) {
        if (!this.available) {
            return new Promise((resolve) => resolve);
        }

        return new Promise((resolve, reject) => {
            try {
                this.storage.setItem(
                    key,
                    JSON.stringify(value)
                );
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Removes data from the storage system.
     * @param {String} key Key name
     */
    remove(key) {
        this.available && this.storage.removeItem(key);
    }

    /**
     * Calculates the storage system's size per given keys.
     * Includes a "total" key that denotes the total size of the storage.
     * @param  {...String} [keys] Keys to calculate. When omitted, will calculate all keys.
     * @return {Object}
     */
    size(...keys) {
        const all = !keys.length;
        let total = 0;
        all && (keys = Array.from(Array(this.storage.length)).map((item, index) => this.storage.key(index)));

        const result = keys.reduce((result, key) => {
            const length = (key + this.storage.getItem(key)).length;

            result[key] = length;
            all && (total += length);

            return result;
        }, {});

        all && Object.assign(result, { total });

        return result;
    }
}
